<template>
  <v-card flat>
    <v-card-title class="py-0 px-0">
      <v-toolbar
        :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
        class="filter-toolbar"
        flat
      >
        <v-btn
          v-if="posUserHasComponentPermission('Orders', 'ADD') && posUserHasComponentPermission('Pos', 'ACCESS')"
          :icon="$vuetify.breakpoint.xsOnly"
          :to="{name: 'Pos', exact: true}"
          class="primary--text elevation-0 ma-1"
          text
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Order.OrdersAll.Toolbar.Button.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{
            $t('Order.OrdersAll.Toolbar.Button.Title').toLocaleUpperCase($i18n.locale)
          }}</span>
        </v-btn>

        <reports-button
          v-if="posUserHasComponentPermission('Orders', 'MANAGE') && posUser?.Settings?.AllUsers?.Permissions?.CanPrintReportZ && (userCanViewDailyOrders || userCanViewAllOrders || userCanViewFiscalDoc)"
          :disable-tooltip="!$vuetify.breakpoint.xsOnly"
          :fab="$vuetify.breakpoint.xsOnly"
          :hide-label="$vuetify.breakpoint.xsOnly"
          :orders-report-history="posUserHasComponentPermission('ReportsNotify', 'ACCESS')"
          :receipts-report="userCanViewFiscalDoc"
          :receipts-report-history="posUserHasComponentPermission('ReportsNotify', 'ACCESS')"
          depressed
          flat
          orders-report
          orders-report-email
          orders-report-pdf
          orders-report-print
          receipts-report-email
          receipts-report-pdf
          receipts-report-print
          @print-orders-report="printer => printOrderTotalsZ(null, printer)"
          @print-receipts-report="printer => showSelectDateRangeDialog(printer, 'receiptTotalsZ', 'print')"
          @download-pdf-orders-report="() => downloadPdfOrdersReport()"
          @download-pdf-receipts-report="() => showSelectDateRangeDialog(null, 'receiptTotalsZ', 'pdf')"
          @send-email-orders-report="() => showSelectEmailDialog()"
          @send-email-receipts-report="() => showSelectDateRangeDialog(null, 'receiptTotalsZ', 'email')"
        />

        <v-btn
          v-if="posUserHasComponentPermission('Orders', 'MANAGE') && userCanViewAllOrders"
          :disabled="!viewHasData"
          :icon="$vuetify.breakpoint.xsOnly"
          class="teal--text elevation-0 ma-1"
          text
          @click="showOrderMapDialog"
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Order.OrdersToday.Toolbar.Button.Map.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{
            $t('Order.OrdersToday.Toolbar.Button.Map.Title').toLocaleUpperCase($i18n.locale)
          }}</span>
        </v-btn>

        <v-spacer />

        <table-actions
          v-if="userCanDeleteOrders"
          v-model="dataTable.tableActions"
          :disabled="!viewHasData"
          :items.sync="selectedOrders"
        >
          <v-btn
            v-if="userCanDeleteOrders"
            :color="$vuetify.theme.dark ? '#1E1E1E' : 'rgba(0, 0, 0, 0.06)'"
            :disabled="deleteOrderLoading || !selectedOrders.length"
            :loading="deleteOrderLoading"
            :style="$vuetify.breakpoint.xsOnly ? 'width: 36px; min-width: 36px; height: 36px;' : 'width: 48px; min-width: 48px; height: 48px;'"
            class="pa-0 ma-1 elevation-0"
            @click="onDeleteOrdersClick(selectedOrders)"
          >
            <v-icon
              color="red"
              v-text="'delete'"
            />
          </v-btn>
        </table-actions>

        <filter-search v-model="dataTable.search.terms" />

        <filter-options
          v-model="filtersModel"
          :filters="filterData"
          max-width="500"
        />
      </v-toolbar>
    </v-card-title>

    <!--TODAY ORDERS TABLE-->
    <v-data-table
      v-model="selectedOrders"
      :custom-filter="customSearch"
      :footer-props="dataTable.footerProps"
      :headers="dataTableHeaders"
      :height="dataTable.resize.bodyHeight"
      :items="filteredItems"
      :loading="dataTable.loading"
      :loading-text="dataTable.text.loadingText"
      :no-data-text="dataTable.text.noDataText"
      :no-results-text="dataTable.text.noResultsText"
      :options.sync="dataTable.options"
      :search="dataTable.search.terms"
      :show-select="dataTable.tableActions"
      :single-select="false"
      fixed-header
      item-key="Id"
      @current-items="onDataTableCurrentItemsChange"
    >
      <template
        v-if="displayOrdersWarning"
        #body.prepend
      >
        <tr>
          <td
            class="px-0"
            colspan="20"
          >
            <v-alert
              :icon="false"
              :style="$vuetify.theme.dark ? 'background-color: #252525' : 'background-color: #f6f6f6'"
              border="left"
              class="my-0 px-4 py-1"
              colored-border
              type="warning"
              width="100%"
            >
              <v-chip
                v-if="hasOrdersWithUnpaidCardTransactions"
                :outlined="filtersModel.filterOrderNoCardPayment.value !== 2"
                class="pl-2 pr-3 mr-3 my-1"
                color="blue-grey"
                dark
                label
                style="cursor: pointer; height: 38px;"
                @click="onFilterWithPendingCardPayment"
              >
                <span class="">
                  <v-badge
                    color="blue-grey darken-2"
                    inline
                  >
                    <template #badge>
                      <span class="font-weight-bold">{{ ordersCountWithUnpaidCardTransactions }}</span>
                    </template>
                  </v-badge>

                  {{ $tc('Delivery.Button.Shift.Close.PopUp.PendingCardPayments', ordersCountWithUnpaidCardTransactions) }}
                </span>
              </v-chip>

              <v-chip
                v-if="hasOrdersWithoutFiscalDocs"
                :outlined="filtersModel.filterOrderReceipt.value !== 2"
                class="pl-2 pr-3 mr-3 my-1"
                color="purple lighten-2"
                dark
                label
                style="cursor: pointer; height: 38px;"
                @click="onFilterWithoutFiscalDoc"
              >
                <span class="">
                  <v-badge
                    color="purple darken-2"
                    inline
                  >
                    <template #badge>
                      {{ ordersCountWithoutFiscalDocs }}
                    </template>
                  </v-badge>

                  {{ $tc('Delivery.Button.Shift.Close.PopUp.WithoutFiscalDoc', ordersCountWithoutFiscalDocs) }}
                </span>
              </v-chip>
            </v-alert>
          </td>
        </tr>
      </template>

      <template #[`item.source_type`]="{item}">
        <div v-if="getOrderSourceTypeById(item.SourceTypeId)">
          <v-tooltip
            color="indigo lighten-2"
            right
          >
            <template #activator="{on}">
              <div v-on="on">
                <v-badge
                  v-model="item.IsTest"
                  bordered
                  color="deep-orange"
                  overlap
                >
                  <template #badge>
                    <span class="font-weight-bold">TEST</span>
                  </template>

                  <v-avatar size="28">
                    <v-icon
                      v-if="item.SourceTypeId < 100"
                      color="indigo lighten-2"
                    >
                      {{ getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo }}
                    </v-icon>

                    <img
                      v-else
                      :src="getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo"
                    >
                  </v-avatar>
                </v-badge>
              </div>
            </template>

            <div v-if="item.IsTest">
              {{ $t('Common.Misc.TestOrder') }}
            </div>
            <span v-if="item.SourceTypeId < 100">
              {{ getOrderSourceTypeLangById(item.SourceTypeId) && getOrderSourceTypeLangById(item.SourceTypeId).Desc }}
            </span>
            <span v-else>
              {{ getOrderSourceTypeById(item.SourceTypeId).desc }}
            </span>
          </v-tooltip>
        </div>
      </template>

      <template #[`item.daily_check`]="{item}">
        <v-chip
          v-if="item.DailyCheck && appConfig.LOCATION_DATA.ShowDailyCheck"
          class="px-1"
          label
          x-small
        >
          {{ item.DailyCheck }}
        </v-chip>
      </template>

      <template #[`item.order_id`]="{item}">
        <div class="d-flex align-center">
          <template v-if="item.ExternalPlatform && item.ExternalCatalogId && getExternalOrderRawUrl(item) && userIsSuperAdmin">
            <div class="d-inline-flex mr-2">
              <v-menu
                bottom
                offset-y
                transition="slide-y-transition"
              >
                <template #activator="{on: onMenu}">
                  <v-tooltip
                    color="indigo"
                    top
                  >
                    <template #activator="{on: onTooltip}">
                      <v-btn
                        class="indigo"
                        dark
                        icon
                        small
                        target="_blank"
                        v-on="{...onMenu, ...onTooltip}"
                      >
                        <v-icon small>
                          mdi-refresh
                        </v-icon>
                      </v-btn>
                    </template>

                    <span>Retry External Order</span>
                  </v-tooltip>
                </template>

                <v-card>
                  <v-card-text class="pa-2">
                    <v-btn
                      block
                      class="green my-1"
                      dark
                      text
                      @click="retryExternalOrder(item, false)"
                    >
                      Retry No Broadcast
                    </v-btn>

                    <v-btn
                      block
                      class="primary my-1"
                      text
                      @click="retryExternalOrder(item, true)"
                    >
                      Retry WITH Broadcast
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>

            <div class="d-inline-flex mr-2">
              <v-tooltip
                color="light-green"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :href="getExternalOrderRawUrl(item)"
                    class="light-green"
                    dark
                    icon
                    small
                    target="_blank"
                    v-on="on"
                  >
                    <v-icon small>
                      mdi-code-block-braces
                    </v-icon>
                  </v-btn>
                </template>

                <span>View External Order</span>
              </v-tooltip>
            </div>
          </template>

          <div class="d-inline-flex">
            <div>
              <a
                href="#"
                @click.prevent="getOrderDetails(item)"
              >{{ item.OrderCode }}</a>

              <template v-if="item.IsEdited">
                <v-tooltip
                  color="purple"
                  top
                >
                  <template #activator="{on}">
                    <v-avatar
                      class="ml-1"
                      color="purple"
                      size="16"
                      v-on="on"
                    >
                      <v-icon
                        dark
                        x-small
                        v-text="$t('Order.OrdersAll.ListView.Button.Edit.Icon')"
                      />
                    </v-avatar>
                  </template>

                  <span>{{ $t('Order.OrdersAll.ListView.Button.Edit.Edited') }}</span>
                </v-tooltip>
              </template>

              <div
                v-if="item.ExternalPlatform"
                class="text-caption text-uppercase"
              >
                #{{ item.ExternalOrderId }}
              </div>

              <div
                v-if="item.MarketplaceOrderCode"
                class="text-caption text-uppercase"
              >
                #{{ item.MarketplaceOrderCode }}
              </div>

              <div
                v-if="item.QrData"
                class="text-caption"
              >
                {{ item.QrData }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #[`item.icons`]="{item}">
        <v-tooltip
          :color="parseInt(item.CustomerId) === 0 ? 'grey darken-3' : 'amber darken-3'"
          right
        >
          <template #activator="{on}">
            <v-icon
              :color="parseInt(item.CustomerId) === 0 ? 'grey' : 'amber'"
              class="text--lighten-2"
              small
              v-on="on"
            >
              verified_user
            </v-icon>
          </template>
          <span>
            {{ parseInt(item.CustomerId) === 0 ? $t('Customer.CustomerType.Visitor') : $t('Customer.CustomerType.Registered') }}
          </span>
        </v-tooltip>

        <div v-if="parseInt(item.AddressId) > -1 && item.Address && parseInt(item.OrderTypeId) === 1">
          <v-icon
            color="light-green lighten-1"
            small
          >
            {{ getAddressType(item.Address)?.Icon || 'location_on' }}
          </v-icon>
        </div>

        <div v-if="item.Telephone && $options.filters.phone(item.Telephone)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>

        <div v-if="item.Telephone2 && $options.filters.phone(item.Telephone2)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>

        <div v-if="item.Telephone3 && $options.filters.phone(item.Telephone3)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>
      </template>

      <template #[`item.customer`]="{item}">
        <div
          v-if="posUserHasComponentPermission('CustomerDetails', 'ACCESS') && parseInt(item.CustomerId) !== 0"
          :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'"
          :style="customerColumnMaxWidth"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer;"
          @click="$bus.$emit('show-customer-details-dialog', item.CustomerId)"
        >
          {{ item.FullName }}
        </div>

        <template v-else>
          <div
            :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'"
            :style="customerColumnMaxWidth"
            style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
          >
            {{
              parseInt(item.CustomerId) !== 0 || item.FullName.trim() ? item.FullName : $t('Customer.CustomerType.Visitor')
            }}
          </div>
        </template>

        <div
          v-if="!isQrOrder(item) && parseInt(item.AddressId) > -1 && item.Address && parseInt(item.OrderTypeId) === 1"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          <address-display :address="item?.Address" />
        </div>

        <div
          v-else-if="isQrOrder(item) && parseInt(item.OrderTypeId) === 1"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          {{ item.Comment }}
        </div>

        <div
          v-if="item.Telephone || item.Telephone2 || item.Telephone3"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          <template v-if="item.Telephone && $options.filters.phone(item.Telephone)">
            {{ item.Telephone | phone }}<br>
          </template>

          <template v-if="item.Telephone2 && $options.filters.phone(item.Telephone2)">
            {{ item.Telephone2 | phone }}<br>
          </template>

          <template v-if="item.Telephone3 && $options.filters.phone(item.Telephone3)">
            {{ item.Telephone3 | phone }}<br>
          </template>
        </div>
      </template>

      <template #[`item.staff_id`]="{item}">
        <user-chip
          v-if="item.StaffId > 0"
          :color="item.StaffColor"
          :full-name="item.StaffFirstname + ' ' + item.StaffLastname"
          :short-name="item.StaffShortname"
        />
      </template>

      <template #[`item.status`]="{item}">
        <div class="my-1">
          <order-status-chip
            :order="item"
            dense
            min-width="186"
          />
        </div>

        <div class="my-1">
          <order-status-actions
            :can-update-order-time="canUpdateOrderTime(item)"
            :disabled="item.disabled"
            :order="item"
          />
        </div>
      </template>

      <template #[`item.order_type`]="{item}">
        <div class="my-1">
          <div
            v-if="parseInt(item.SourceTypeId) === SourceTypeEnum.TABLE && item.Table"
            class="font-weight-medium"
            style="font-size: 11px"
          >
            {{ item.Table?.TableAreaName }} #{{ item.Table?.TableName }}
          </div>

          <order-type-chip
            :order="item"
            dense
          />
        </div>

        <div class="my-1">
          <order-delivery-status-chip
            :order="item"
            dense
          />
        </div>
      </template>

      <template #[`item.print_notify`]="{item}">
        <div
          v-if="!!item?.Prints?.length && isAppPrintLogVisible"
          class="my-1"
        >
          <template v-for="(print, idx) in item.Prints">
            <div :key="idx">
              <v-tooltip
                :color="getPrintNotifyStatus(print).Color"
                :disabled="!getOrderMachineGroups(print)?.length && !print.PrinterNames?.length"
                top
              >
                <template #activator="{on}">
                  <status-chip
                    :avatar-color="getPrintNotifyStatus(print).AvatarColor"
                    :color="getPrintNotifyStatus(print).Color"
                    :icon="getPrintNotifyStatus(print).AvatarIcon"
                    :label="getPrintNotifyStatus(print).Label"
                    :min-width="180"
                    :tooltip="getPrintNotifyStatus(print).Tooltip"
                    x-dense
                    v-on="on"
                  />
                </template>

                <div>
                  <div>
                    <h4>{{ $tc('Settings.Machine.Title', 0) }}</h4>
                    <template v-for="(group, index) in getOrderMachineGroups(print)">
                      <v-chip
                        :key="`machine-group-${index}`"
                        class="pa-1 mb-1 d-flex"
                        color="grey darken-3"
                        dark
                        label
                        small
                      >
                        {{ group.Name }} ({{ group.MachinesCount }} PC)
                      </v-chip>

                      <template v-for="(machine, mIdx) in getMachineGroupMachineNames(group.Guid)">
                        <v-chip
                          :key="`machine-group-${index}-machine-${mIdx}`"
                          class="ml-6 pa-1 mb-1 d-flex"
                          color="blue-grey darken-1"
                          dark
                          label
                          small
                        >
                          {{ machine }}
                        </v-chip>
                      </template>
                    </template>
                  </div>

                  <div class="mt-4">
                    <h4>{{ $tc('Settings.Printer.Title', 0) }}</h4>
                    <template v-for="(printer, index) in print.PrinterNames">
                      <v-chip
                        :key="`printer-${index}`"
                        class="pa-1 mb-1 d-flex"
                        color="grey darken-3"
                        dark
                        label
                        small
                      >
                        {{ printer }}
                      </v-chip>
                    </template>
                  </div>
                </div>
              </v-tooltip>
            </div>
          </template>
        </div>
      </template>

      <template #[`item.order_total`]="{item}">
        <v-badge
          :class="{'pulse-badge': orderIsGeneratingFiscalDocument(item)}"
          :color="orderFiscalDocumentBadgeColor(item)"
          :value="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && (orderHasFiscalDocument(item, true) || (orderCanGenerateFiscalDocument(item) && orderIsRefundable(item)) || orderIsGeneratingFiscalDocument(item) )"
          class="receipts-badge"
          offset-x="8"
          offset-y="2"
          right
        >
          <v-tooltip
            v-if="(appHasFiscalDocsEnabled && appHasFiscalDocsProvider) || orderHasFiscalDocument(item, true)"
            :color="orderFiscalDocumentBadgeColor(item)"
            content-class="text-center"
            max-width="350"
            right
          >
            <template #activator="{on}">
              <div
                class="d-inline-block"
                v-on="on"
              >
                <template v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
                  <span class="red--text">{{ orderTransactionsTotal(item) | currency }}</span> <span>/</span> <span class="blue--text">{{ item.Price | currency }}</span>
                </template>
                <template v-else>
                  {{ item.Price | currency }}
                </template>
              </div>
            </template>

            <div v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
              {{ $t('Order.Total.PartiallyPaid') }}
            </div>

            <div v-else-if="orderHasReceipt(item, orderLatestFiscalDocumentHasError(item)) && orderHasInvoice(item, orderLatestFiscalDocumentHasError(item))">
              <div>
                {{ $t('Order.Total.Receipt') }}

                <v-chip
                  v-if="orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                  class="px-2 ml-2 mt-1 float-end"
                  x-small
                >
                  {{ orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) }}
                </v-chip>
              </div>

              <div>
                {{ $t('Order.Total.Invoice') }}

                <v-chip
                  v-if="orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                  class="px-2 ml-2 mt-1 float-end"
                  x-small
                >
                  {{ orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) }}
                </v-chip>
              </div>
            </div>

            <div v-else-if="orderHasReceipt(item, orderLatestFiscalDocumentHasError(item))">
              {{ $t('Order.Total.Receipt') }}

              <v-chip
                v-if="orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                class="px-2 ml-2 mt-1 float-end"
                x-small
              >
                {{ orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) }}
              </v-chip>
            </div>

            <div v-else-if="orderHasInvoice(item, orderLatestFiscalDocumentHasError(item))">
              {{ $t('Order.Total.Invoice') }}

              <v-chip
                v-if="orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                class="px-2 ml-2 mt-1 float-end"
                x-small
              >
                {{ orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) }}
              </v-chip>
            </div>

            <div v-else-if="orderIsGeneratingFiscalDocument(item)">
              {{ $t('Order.Total.GeneratingDoc') }}
            </div>

            <div v-else>
              {{ $t('Order.Total.NoFiscalDoc') }}
            </div>

            <div
              v-if="orderLatestFiscalDocumentHasError(item)"
              class="mt-2"
            >
              <template v-for="receipt in orderFiscalDocuments(item, true)">
                <div
                  :key="`receipt-${receipt.ReceiptId}`"
                  class="mb-1"
                >
                  <template v-for="(error, index) in orderFiscalDocumentErrors(receipt)">
                    <div
                      :key="`receipt-${receipt.ReceiptId}-error-${index}`"
                      class="text-left mb-1"
                    >
                      <span class="font-weight-bold">{{ error.code }}</span> - {{ error.message }}
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </v-tooltip>

          <div
            v-else
            class="d-inline-block"
          >
            <template v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
              <span class="red--text">{{ orderTransactionsTotal(item) | currency }}</span> <span>/</span> <span class="blue--text">{{ item.Price | currency }}</span>
            </template>
            <template v-else>
              {{ item.Price | currency }}
            </template>
          </div>

          <template #badge>
            {{ item?.ReceiptsCount || '' }}
          </template>
        </v-badge>
      </template>

      <template #[`item.order_payment`]="{item}">
        <order-payments-icon
          :item="item"
          chip
          class="ml-1"
          dense
        />
      </template>

      <template #[`item.date_added`]="{item}">
        <template v-if="item.IsScheduled">
          <span>{{ item.ScheduledDateAt }}</span>

          <div class="grey--text">
            {{ item.ScheduledTimeAt }}
          </div>
        </template>

        <template v-else>
          <span>{{ item.DateAdded }}</span>

          <div class="grey--text">
            {{ item.TimeAdded }}
          </div>
        </template>
      </template>

      <template
        v-if="appHasWebhooksOrderActive && (userIsOwner || userIsSuperAdmin)"
        #[`item.webhook_successful`]="{item}"
      >
        <status-chip
          v-if="item?.Webhook?.Successful !== null"
          :avatar-color="getOrderWebhookStatus(item).Color"
          :chip-style="userIsSuperAdmin ? {cursor: 'pointer'} : ''"
          :color="getOrderWebhookStatus(item).Color"
          :icon="getOrderWebhookStatus(item).Icon"
          :icon-only="true"
          :label="getOrderWebhookStatus(item).Name"
          :tooltip="getOrderWebhookStatus(item).Tooltip"
          dense
          @click="getOrderWebhookHistory(item)"
        />
      </template>

      <template #[`item.actions`]="{item}">
        <v-btn
          v-if="dataTable.tableActions"
          :small="$vuetify.breakpoint.xsOnly"
          class="blue-grey lighten-2 elevation-2 ma-0"
          dark
          disabled
          icon
        >
          <v-icon :small="$vuetify.breakpoint.xsOnly">
            menu
          </v-icon>
        </v-btn>

        <v-speed-dial
          v-else
          v-model="item.fab"
          direction="left"
          transition="slide-x-reverse-transition"
        >
          <template #activator>
            <v-btn
              :small="$vuetify.breakpoint.xsOnly"
              class="blue-grey lighten-2 elevation-2 ma-0"
              dark
              icon
            >
              <v-icon
                v-if="item.fab"
                :small="$vuetify.breakpoint.xsOnly"
              >
                close
              </v-icon>
              <v-icon
                v-else
                :small="$vuetify.breakpoint.xsOnly"
              >
                menu
              </v-icon>
            </v-btn>
          </template>

          <template v-if="orderPaymentDialogAvailable(item)">
            <v-tooltip
              color="indigo"
              content-class="text-center"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t(`Order.OrdersToday.ListView.Button.OrderPayment.Color`)"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="showOrderPaymentDialog(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t(`Order.OrdersToday.ListView.Button.OrderPayment.Icon`) }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t(`Order.OrdersToday.ListView.Button.OrderPayment.Title`) }}</span>
            </v-tooltip>
          </template>

          <issue-receipt-document-button
            v-if="issueReceiptDocumentVisible(item) && (orderCanGenerateFiscalDocument(item) || (!orderCanGenerateFiscalDocument(item) && orderHasFiscalDocument(item)))"
            :badge-offset-x="20"
            :badge-offset-y="20"
            :can-cancel-document="posUserCan('RECEIPTS', 'MANAGE')"
            :can-convert-receipt="appCanConvertReceiptToInvoice"
            :can-issue-document="orderCanGenerateFiscalDocument(item)"
            :document-id="orderLatestFiscalDocument(item)?.ReceiptId"
            :document-type="orderLatestFiscalDocument(item)?.ReceiptType"
            :document-url="orderLatestFiscalDocument(item)?.DocumentUrl"
            :from="EmailNotifyFromEnum.ORDER"
            :icon-name="orderHasFiscalDocument(item) ? 'mdi-receipt-text-edit-outline' : 'mdi-receipt-text-plus-outline'"
            :invoice="appHasInvoiceDocEnabled && appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE)"
            :label="orderHasFiscalDocument(item) ? $t('Common.Button.ManageDocument').toLocaleUpperCase($i18n.locale) : $t('Common.Button.IssueDocument').toLocaleUpperCase($i18n.locale)"
            :order="item"
            :order-id="item?.Id"
            :receipt="appHasReceiptDocEnabled && appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT)"
            :small="$vuetify.breakpoint.xsOnly"
            :tooltip="orderHasFiscalDocument(item) ? orderHasReceipt(item) ? $t('Common.Button.ManageReceipt') : orderHasInvoice(item) ? $t('Common.Button.ManageInvoice') : $t('Common.Button.ManageFnb') : $t('Common.Button.IssueDocument')"
            :tooltip-open-delay="150"
            can-cancel-fnb
            hide-label
            icon
            tooltip-position="top"
            @view-document="data => $bus.$emit('fiscal-document:view', data.DocUrl)"
            @issue-document="data => $bus.$emit('fiscal-document:create', item, data.DocType, (data.Print === false || data.Print === true) ? {Print: data.Print} : null)"
            @cancel-document="data => $bus.$emit('fiscal-document:cancel', orderLatestFiscalDocument(item), data)"
            @print-document="data => $bus.$emit('fiscal-document:print', data.DocId)"
            @email-document="data => $bus.$emit('fiscal-document:email', data.DocId, item, data)"
            @convert-receipt="data => $bus.$emit('fiscal-document:convert-to-invoice', orderLatestFiscalDocument(item), data)"
          />

          <print-button
            v-if="printOrderDocumentVisible(item)"
            :small="$vuetify.breakpoint.xsOnly"
            :tooltip-open-delay="150"
            hide-label
            icon
            menu-offset-y
            open-on-hover
            tooltip-position="top"
            @printer-selected="printer => printOrder(item, printer)"
          />

          <print-preparation-button
            v-if="printOrderPreparationVisible(item)"
            :order-print-groups="item.PrintGroups || []"
            :small="$vuetify.breakpoint.xsOnly"
            :tooltip-open-delay="150"
            hide-label
            icon
            menu-offset-y
            open-on-hover
            tooltip-position="top"
            @printer-group-selected="printerGroup => printOrderPreparation(item, printerGroup)"
          />

          <template v-if="parseInt(item.SourceTypeId) !== SourceTypeEnum.TABLE">
            <template
              v-if="parseInt(item.DeliveryStaffId) === -1 && parseInt(item.StatusId) !== OrderStatusEnum.NotComplete && posUserHasComponentPermission('Orders', 'MANAGE') && posUserHasComponentPermission('DeliveryStaff', 'MANAGE')"
            >
              <v-tooltip
                :color="$t('Order.OrdersToday.ListView.Button.AddTo.Color')"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersToday.ListView.Button.AddTo.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="showAddOrderToDeliveryStaff(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersToday.ListView.Button.AddTo.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{ $t('Order.OrdersToday.ListView.Button.AddTo.Title') }}</span>
              </v-tooltip>
            </template>

            <template
              v-if="parseInt(item.DeliveryStaffId) > -1 && deliveryStaffHasActiveShift(item) && parseInt(item.StatusId) !== OrderStatusEnum.NotComplete && posUserHasComponentPermission('Orders', 'MANAGE') && posUserHasComponentPermission('DeliveryStaff', 'MANAGE')"
            >
              <v-tooltip
                :color="$t('Order.OrdersToday.ListView.Button.RemoveFrom.Color')"
                content-class="text-center"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersToday.ListView.Button.RemoveFrom.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="showRemoveOrderFromDeliveryStaffDialog(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersToday.ListView.Button.RemoveFrom.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{
                  $t('Order.OrdersToday.ListView.Button.RemoveFrom.Title')
                }}<br>{{
                  item.DeliveryStaffFirstname + ' ' + item.DeliveryStaffLastname | sanitizeDiacritics | uppercase
                }}</span>
              </v-tooltip>
            </template>

            <template v-if="!SourceTypeEnum.IS_MARKETPLACE_SOURCE(item.SourceTypeId) && canEditOrder(item)">
              <v-tooltip
                :color="$t('Order.OrdersAll.ListView.Button.Edit.Color')"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersAll.ListView.Button.Edit.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="showOrderEditDialog(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersAll.ListView.Button.Edit.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{ $t('Order.OrdersAll.ListView.Button.Edit.Title') }}</span>
              </v-tooltip>
            </template>
          </template>

          <v-tooltip
            :color="$t('Order.OrdersAll.ListView.Button.View.Color')"
            top
          >
            <template #activator="{on}">
              <v-btn
                :class="$t('Order.OrdersAll.ListView.Button.View.Color')"
                :small="$vuetify.breakpoint.xsOnly"
                dark
                icon
                @click="getOrderDetails(item)"
                v-on="on"
              >
                <v-icon :small="$vuetify.breakpoint.xsOnly">
                  {{ $t('Order.OrdersAll.ListView.Button.View.Icon') }}
                </v-icon>
              </v-btn>
            </template>

            <span>{{ $t('Order.OrdersAll.ListView.Button.View.Title') }}</span>
          </v-tooltip>

          <template v-if="parseInt(item.StatusId) !== OrderStatusEnum.Canceled && posUserHasComponentPermission('Orders', 'DELETE')">
            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.Cancel.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.Cancel.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="onCancelOrderClick(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.Cancel.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.Cancel.Title') }}</span>
            </v-tooltip>
          </template>

          <template v-if="parseInt(item.StatusId) === OrderStatusEnum.Canceled && posUserHasComponentPermission('Orders', 'MANAGE') && (userIsOwner || userIsSuperAdmin)">
            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.Revert.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.Revert.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="onRevertOrderClick(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.Revert.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.Revert.Title') }}</span>
            </v-tooltip>
          </template>

          <template
            v-if="!SourceTypeEnum.IS_MARKETPLACE_SOURCE(item.SourceTypeId) && (parseInt(item.StatusId) === OrderStatusEnum.Completed || parseInt(item.StatusId) === OrderStatusEnum.Canceled) && userCanDeleteOrder"
          >
            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="onDeleteOrderClick(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.Delete.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.Delete.Title') }}</span>
            </v-tooltip>
          </template>
        </v-speed-dial>
      </template>
    </v-data-table>

    <order-change-status-dialog
      :cancel-order-confirm-dialog-visible.sync="cancelOrderConfirmDialogVisible"
      :selected-order="!Array.isArray(selectedOrder) ? selectedOrder : undefined"
    />

    <confirm-dialog
      :html-content="$t('Order.OrderChangeStatusDialog.RevertStatus.Subtitle', {orderCode: selectedOrder?.OrderCode || ''})"
      :html-title="$t('Order.OrderChangeStatusDialog.RevertStatus.Title')"
      :visible.sync="revertOrderStatusConfirmDialogVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      @confirm-dialog-yes="revertOrderStatus"
    />

    <order-delete-dialog
      v-if="appConfig.LOCATION_DATA.HasForceDeleteOrders && posUserHasComponentPermission('Orders', 'DELETE')"
      :is-delete-and-copy-order="isDeleteAndCopyOrder"
      :selected-order="selectedOrder"
      :visible.sync="deleteOrderConfirmDialogVisible"
      @order-deleted="onOrdersDelete"
    />

    <order-payment-dialog
      v-if="selectedOrder"
      :customer-id="selectedOrder.CustomerId"
      :order="selectedOrder"
      :order-id="selectedOrder.Id"
      :order-total="selectedOrder.OrderTotal"
      :source-type="selectedOrder.SourceTypeId"
      :transactions="selectedOrder.PaymentTransactions"
      :visible.sync="orderPaymentDialogVisible"
    />

    <select-delivery-staff-dialog
      :selected-delivery-staff.sync="selectedDeliveryStaff"
      :visible.sync="selectedDeliveryStaffDialogVisible"
    />

    <confirm-order-edit-dialog
      :selected-order="selectedOrder"
      :visible.sync="confirmDialogEditOrderVisible"
      @order-edit="onEditAdvanced"
      @order-edit-copy="onEditCopy"
      @order-edit-delete="onEditDelete"
      @order-edit-payments="showOrderPaymentDialog"
    />

    <confirm-dialog
      :html-content="`${$t('Order.OrdersToday.ListView.Button.RemoveFrom.PopUp.Content')} <strong>${selectedOrder ? selectedOrder.DeliveryStaffFirstname : ''} ${selectedOrder ? selectedOrder.DeliveryStaffLastname : ''}</strong>;`"
      :html-title="$t('Order.OrdersToday.ListView.Button.RemoveFrom.PopUp.Title')"
      :visible.sync="confirmDialogRemoveOrderFromDeliveryStaffVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="delete-order-on-yes-btn"
      @delete-order-on-yes-btn="removeOrderFromDeliveryStaff"
    />

    <orders-map-dialog
      v-if="ordersMapDialogVisible"
      :orders-map-dialog-visible.sync="ordersMapDialogVisible"
    />

    <export-totals-z-dialog
      :action="selectedPrinter?.action || 'print'"
      :select-guid="true"
      :select-range="false"
      :select-staff="true"
      :select-type="selectedPrinter?.type === 'receiptTotalsZ'"
      :type="selectedPrinter?.type || 'receiptTotalsZ'"
      :visible.sync="selectDateRangeDialogVisible"
      @date-range="onPrintReceiptTotalsZDateRangeSelected"
    />

    <order-webhook-history-dialog
      v-if="userIsSuperAdmin && webhookHistoryDialogVisible"
      :data="selectedOrderWebhookHistory"
      :visible.sync="webhookHistoryDialogVisible"
      @retry="orderWebhookRetry"
    />

    <email-select-dialog
      :extra-data="emailSelectDialogExtraData"
      :visible.sync="emailSelectDialogVisible"
      use-user-email
      @save:dialog="onEmailSelected"
    />
  </v-card>
</template>

<script>
import OrdersCommon from '@/mixins/orders/common'
import SelectDeliveryStaffDialog from '@/components/delivery/SelectDeliveryStaffDialog.vue'
import OrdersMapDialog from '@/components/orders/OrdersMapDialog'
import PrintButton from '@/components/common/print/PrintButton.vue'
import Translatable from '@/mixins/translatable'
import FilterCommon from '@/mixins/filter/filterCommon'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import PrintPreparationButton from '@/components/common/print/PrintPreparationButton.vue'
import DataTable from '@/mixins/data-table/dataTable'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import greekUtils from '@/lib/greek-utils'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum'
import TableActions from '@/components/common/filter/TableActions.vue'
import IssueReceiptDocumentButton from '@/components/common/receipt/IssueReceiptDocumentButton.vue'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import OrderPaymentDialog from '@/components/payments/orders/OrderPaymentDialog.vue'
import OrderPaymentsIcon from '@/components/orders/common/OrderPaymentsIcon.vue'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum'
import PinManagerEnum from '@/api/Enums/PinManagerEnum'
import {getContrastColor} from '@/lib/color'
import OrderStatusChip from '@/components/orders/common/OrderStatusChip.vue'
import OrderTypeChip from '@/components/orders/common/OrderTypeChip.vue'
import OrderDeliveryStatusChip from '@/components/orders/common/OrderDeliveryStatusChip.vue'
import ExportTotalsZDialog from '@/components/common/ExportTotalsZDialog.vue'
import StatusChip from '@/components/common/StatusChip.vue'
import OrderWebhookHistoryDialog from '@/components/orders/OrderWebhookHistoryDialog.vue'
import EmailNotifyFromEnum from '@/api/Enums/EmailNotifyFromEnum.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import AddressCommon from '@/mixins/maps/addressCommon.js'
import OrderStatusActions from '@/components/orders/common/OrderStatusActions.vue'
import AddressDisplay from '@/components/common/AddressDisplay.vue'
import * as SocketOrderHelper from '@/api/SocketOrderHelper.js'
import SocketCommand from '@/api/SocketCommand.js'
import {clone} from '@/lib/helper/helper.js'
import ConfirmOrderEditDialog from '@/components/orders/ConfirmOrderEditDialog.vue'
import ReportsButton from '@/components/common/print/ReportsButton.vue'
import EmailSelectDialog from '@/components/common/EmailSelectDialog.vue'
import emailSelectDialog from '@/components/common/EmailSelectDialog.vue'
import UserChip from '@/components/common/UserChip.vue'

export default {
  components: {
    UserChip,
    EmailSelectDialog,
    ReportsButton,
    ConfirmOrderEditDialog,
    AddressDisplay,
    OrderStatusActions,
    OrderWebhookHistoryDialog,
    StatusChip,
    ExportTotalsZDialog,
    OrderDeliveryStatusChip,
    OrderTypeChip,
    OrderStatusChip,
    OrderPaymentsIcon,
    OrderPaymentDialog,
    IssueReceiptDocumentButton,
    TableActions,
    FilterSearch,
    PrintPreparationButton,
    FilterOptions,
    PrintButton,
    'select-delivery-staff-dialog': SelectDeliveryStaffDialog,
    'orders-map-dialog'           : OrdersMapDialog
  },
  directives: {},
  mixins    : [AddressCommon, OrdersCommon, Translatable, FilterCommon, DataTable],
  data() {
    return {
      dataTable: {
        dataStore    : 'ordersDataToday',
        socketCommand: {
          list: window.SocketCommand.Order.DailyList
        },
        resize: {
          enabled        : true,
          debounceTimeout: null,
          offset         : -12,
          bodyHeight     : undefined
        },
        options: {
          serverSideEnabled: false,
          defaultSortBy    : 'order_id',
          sortBy           : ['order_id'],
          sortDesc         : [true]
        },
        rejectHeaders: {
          xsOnly   : ['icons'],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: []
      },

      filtersModel: {
        lang           : 'Order.OrdersToday',
        filterOrderType: {
          value: 0,
          items: this.$t('Order.OrdersToday.Filter.filterOrderType.Items')
        },
        filterOrderReceipt: {
          value   : 0,
          items   : this.$t('Order.OrdersToday.Filter.filterOrderReceipt.Items'),
          disabled: true
        },
        filterOrderNoCardPayment: {
          value   : 0,
          items   : this.$t('Order.OrdersToday.Filter.filterOrderNoCardPayment.Items'),
          disabled: true
        },
        filterWebhookSuccessful: {
          key     : 'filter_webhook_successful',
          value   : 0,
          items   : this.$t('Order.OrdersToday.Filter.filterWebhookSuccessful.Items'),
          disabled: true
        },
        filterIsScheduled: {
          value: 0,
          items: this.$t('Order.OrdersToday.Filter.filterIsScheduled.Items')
        },
        filterHasDeliveryStaff: {
          value: 0,
          items: this.$t('Order.OrdersToday.Filter.filterHasDeliveryStaff.Items')
        },
        filterOrderStatus: {
          value: [],
          items: this.$t('Order.OrdersToday.Filter.filterOrderStatus.Items').filter(o => o.Id > 0)
        },
        filterSourceType: {
          value: null,
          items: []
        },
        filterPaymentType: {
          value: null,
          items: []
        },
        filterStaff: {
          value: null,
          items: []
        }
      },
      selectedDeliveryStaff                           : null,
      selectedDeliveryStaffDialogVisible              : false,
      confirmDialogRemoveOrderFromDeliveryStaffVisible: false,
      ordersMapDialogVisible                          : false,
      selectDateRangeDialogVisible                    : false,
      emailSelectDialogVisible                        : false,
      emailSelectDialogExtraData                      : null,
      selectedPrinter                                 : null
    }
  },
  computed: {
    emailSelectDialog() {
      return emailSelectDialog
    },
    EmailNotifyFromEnum() {
      return EmailNotifyFromEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    SourceTypeEnum() {
      return SourceTypeEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },
    OrderStatusEnum() {
      return OrderStatusEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },
    customerColumnMaxWidth() {
      return this.$vuetify.breakpoint.lgAndDown ? 'max-width: 200px; width: 200px;' : 'max-width: 310px;  width: 310px;'
    },
    displayOrdersWarning() {
      return this.userIsSuperAdmin
          && this.appHasFiscalDocsProvider
          && this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD
          && (this.hasOrdersWithoutFiscalDocs || this.hasOrdersWithUnpaidCardTransactions)
    },
    getOrdersWarningText() {
      const pendingCardPayment = this.$tc('Delivery.Button.Shift.Close.PopUp.PendingCardPayments', this.ordersCountWithUnpaidCardTransactions, {ordersCount: this.ordersCountWithUnpaidCardTransactions})
      const withoutFiscalDocs = this.$tc('Delivery.Button.Shift.Close.PopUp.WithoutFiscalDoc', this.ordersCountWithoutFiscalDocs, {ordersCount: this.ordersCountWithoutFiscalDocs})

      if (this.hasOrdersWithUnpaidCardTransactions && this.hasOrdersWithoutFiscalDocs) return pendingCardPayment + ' ' + withoutFiscalDocs
      if (this.hasOrdersWithUnpaidCardTransactions) return pendingCardPayment
      if (this.hasOrdersWithoutFiscalDocs) return withoutFiscalDocs

      return ''
    },
    ordersCountWithoutFiscalDocs() {
      const orders = this.filteredItems || []
      const ordersFiltered = orders
        .filter(o => parseInt(o.StatusId) !== OrderStatusEnum.Canceled)
        .filter(o => !o.IsGeneratingReceipt)
        .filter(o => !o.ReceiptsCount)

      return ordersFiltered.length
    },
    hasOrdersWithoutFiscalDocs() {
      return this.ordersCountWithoutFiscalDocs > 0
    },
    ordersCountWithUnpaidCardTransactions() {
      const orders = this.filteredItems || []
      const ordersFiltered = orders
        .filter(o => !o?.ExternalOrderId && o?.PaymentTransactions?.some(trx => (PaymentTypeEnum.isDeliveryCard(trx.PaymentType) || PaymentTypeEnum.isEftPosPaymentType(trx.PaymentType)) && trx?.Status !== 'APPROVED'))

      return ordersFiltered.length
    },
    hasOrdersWithUnpaidCardTransactions() {
      return this.ordersCountWithUnpaidCardTransactions > 0
    },
    filteredItems() {
      const items = this.dataTable?.data || []
      const filterOrderType = this.filtersModel.filterOrderType.value
      const filterHasDeliveryStaff = this.filtersModel.filterHasDeliveryStaff.value
      const filterIsScheduled = this.filtersModel.filterIsScheduled.value
      const filterOrderReceipt = this.filtersModel.filterOrderReceipt.value
      const filterOrderNoCardPayment = this.filtersModel.filterOrderNoCardPayment.value
      const filterSourceType = this.filtersModel.filterSourceType.value
      const filterPaymentType = this.filtersModel.filterPaymentType.value
      const filterOrderStatus = this.filtersModel.filterOrderStatus.value
      const filterWebhookSuccessful = this.filtersModel.filterWebhookSuccessful.value
      const filterStaff = this.filtersModel.filterStaff.value

      return items
        .map(o => {
          o.isSelectable = (parseInt(o.StatusId) === OrderStatusEnum.Completed || parseInt(o.StatusId) === OrderStatusEnum.Canceled) && this.userCanDeleteOrders
          return o
        })
        .filter(o => this.dailyOrdersVisibleStatuses.includes(o.StatusId))
        .filter(o => filterOrderType ? o.OrderTypeId === filterOrderType && parseInt(o.SourceTypeId) !== 4 : true)
        .filter(o => filterStaff ? o.StaffId === filterStaff : true)
        .filter(o => filterHasDeliveryStaff ? filterHasDeliveryStaff === 1 ? o.DeliveryStaffId > -1 : o.DeliveryStaffId === -1 : true)
        .filter(o => filterIsScheduled ? filterIsScheduled === 1 ? o.IsScheduled : !o.IsScheduled : true)
        .filter(o => filterOrderReceipt ? filterOrderReceipt === 1 ? this.orderHasFiscalDocument(o) : filterOrderReceipt === 3 ? o.ReceiptsCount > 1 : !this.orderHasFiscalDocument(o) : true)
        .filter(o => filterOrderNoCardPayment ? filterOrderNoCardPayment === 1 ? !this.orderIsWithUnpaidCardTransactions(o) && o.PaymentTransactions?.some(trx => (PaymentTypeEnum.isDeliveryCard(trx.PaymentType) || PaymentTypeEnum.isEftPosPaymentType(trx.PaymentType))) : this.orderIsWithUnpaidCardTransactions(o) && o?.PaymentTransactions?.some(trx => (PaymentTypeEnum.isDeliveryCard(trx.PaymentType) || PaymentTypeEnum.isEftPosPaymentType(trx.PaymentType))) : true)
        .filter(o => filterWebhookSuccessful ? filterWebhookSuccessful === 1 ? o.Webhook?.Successful : !o.Webhook?.Successful : true)
        .filter(o => filterSourceType ? o.SourceTypeId === filterSourceType : true)
        .filter(o => filterPaymentType ? this.orderHasPaymentTypeTransaction(o, filterPaymentType) : true)
        .filter(o => filterOrderStatus?.length > 0 ? filterOrderStatus?.includes(o.StatusId) && !o.DeletedAt : true)
    },

    filterStaffList() {
      const users = clone(window?.appConfig?.POS_USERS || [])
      return users.map(o => {
        return {
          StaffId      : o.Id,
          StaffFullname: o.Fullname,
          Description  : o.Uid,
          Avatar       : {
            Icon : o.Active ? 'mdi-account-check-outline' : !o.Uid ? 'mdi-account-group' : 'mdi-account-alert-outline',
            Color: o.Active ? 'success' : !o.Uid ? 'info' : 'grey darken-1'
          }
        }
      })
    },


    deliveryStaffList() {
      return window.Vue?.deliveryStaffItems || []
    },
    dailyOrdersVisibleStatuses() {
      return this.posUser?.Settings?.Orders?.VisibleStatuses || [OrderStatusEnum.Received, OrderStatusEnum.Accepted, OrderStatusEnum.Preparation, OrderStatusEnum.Delivery, OrderStatusEnum.Completed, OrderStatusEnum.Canceled]
    }
  },
  watch: {
    selectedDeliveryStaff(newVal) {
      if (newVal) {
        window.callAS(window.SocketCommand.Staff.Delivery.Shift.AddOrder, {
          Id     : newVal.Id,
          OrderId: this.selectedOrder.Id
        }, window.SocketCommand.Staff.Delivery.All)
        this.selectedOrder = null
        this.selectedDeliveryStaff = null
      }
    }
  },

  created() {
    // console.log('1. created');
    this.$bus.$on('show-add-order-to-delivery-staff-dialog', this.showAddOrderToDeliveryStaff)
    this.$bus.$on('show-remove-order-from-delivery-staff-dialog', this.showRemoveOrderFromDeliveryStaffDialog)
    this.$bus.$on(window.SocketCommand.Receipt.Update, this.onReceiptUpdate)
    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.OrderResult, this.onPaymentTransactionResult)
    this.$bus.$on(window.SocketCommand.Order.DailyList, this.onUpdateSelectedOrder)
    this.$bus.$on(window.SocketCommand.Order.DailyGet, this.onUpdateSelectedOrder)
    this.$bus.$on(window.SocketCommand.Order.Webhook.History, this.getOrderWebhookHistoryResponse)
    this.$bus.$on(window.SocketCommand.Order.Webhook.Retry, this.orderWebhookRetryResponse)
    this.$bus.$on(window.SocketCommand.Ajax.Get, this.onWebhookUpdateData)
    this.$bus.$on(SocketCommand.Order.Report.EmailOrdersReport, this.onEmailReportResponse)
    this.$bus.$on(SocketCommand.Order.Report.EmailReceiptsReport, this.onEmailReportResponse)

    // Init Custom Filter Values
    this.filtersModel.filterSourceType.items = this.sourceTypesList
    this.filtersModel.filterPaymentType.items = this.paymentTypesFiltersList
    this.filtersModel.filterOrderReceipt.disabled = !this.appHasFiscalDocsProvider
    this.filtersModel.filterOrderNoCardPayment.disabled = !this.appHasFiscalDocsProvider
    this.filtersModel.filterWebhookSuccessful.disabled = !this.appHasWebhooksOrderActive
    this.filtersModel.filterStaff.items = this.filterStaffList

    this.dataTable.headers = [
      {
        text     : this.$t('Order.Headers.SourceType'),
        value    : 'source_type',
        align    : 'left',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : '',
        value    : 'daily_check',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1',
        width    : 0
      },
      {
        text     : this.$t('Order.Headers.OrderId'),
        value    : 'order_id',
        align    : 'left',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : '',
        value    : 'icons',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.Customer'),
        value    : 'customer',
        align    : 'left',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.User'),
        value    : 'staff_id',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.Status'),
        value    : 'status',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.OrderType'),
        value    : 'order_type',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      ...this.isAppPrintLogVisible ? [{
        text     : this.$t('Order.Headers.PrintNotify'),
        value    : 'print_notify',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      }] : [],
      {
        text     : this.$t('Order.Headers.Total'),
        value    : 'order_total',
        align    : 'right',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : '',
        value    : 'order_payment',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1',
        width    : 25
      },
      {
        text     : this.$t('Order.Headers.Date'),
        value    : 'date_added',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      ...this.appHasWebhooksOrderActive && (this.userIsOwner || this.userIsSuperAdmin) ? [{
        text     : this.$t('Order.Headers.Transmission'),
        value    : 'webhook_successful',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      }] : [],
      {
        text     : '',
        value    : 'actions',
        align    : 'right',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1',
        width    : 40
      }
    ]
  },
  mounted() {
    // console.log('2. mounted');
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off('show-add-order-to-delivery-staff-dialog', this.showAddOrderToDeliveryStaff)
    this.$bus.$off('show-remove-order-from-delivery-staff-dialog', this.showRemoveOrderFromDeliveryStaffDialog)
    this.$bus.$off(window.SocketCommand.Receipt.Update, this.onReceiptUpdate)
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.OrderResult, this.onPaymentTransactionResult)
    this.$bus.$off(window.SocketCommand.Order.DailyList, this.onUpdateSelectedOrder)
    this.$bus.$off(window.SocketCommand.Order.DailyGet, this.onUpdateSelectedOrder)
    this.$bus.$off(window.SocketCommand.Order.Webhook.History, this.getOrderWebhookHistoryResponse)
    this.$bus.$off(window.SocketCommand.Order.Webhook.Retry, this.orderWebhookRetryResponse)
    this.$bus.$off(window.SocketCommand.Ajax.Get, this.onWebhookUpdateData)
    this.$bus.$off(SocketCommand.Order.Report.EmailOrdersReport, this.onEmailReportResponse)
    this.$bus.$off(SocketCommand.Order.Report.EmailReceiptsReport, this.onEmailReportResponse)
  },
  methods: {
    getContrastColor,

    onEmailReportResponse(response, payload) {
      if (response?.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.EmailReport.Success'),
          type: 'info',
          icon: 'email'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.EmailReport.Error'),
          type: 'error',
          icon: 'email'
        })
      }
    },

    onFilterWithoutFiscalDoc() {
      this.filtersModel.filterOrderReceipt.value = this.filtersModel.filterOrderReceipt.value === 2 ? this.filtersModel.filterOrderReceipt.value = 0 : this.filtersModel.filterOrderReceipt.value = 2
    },

    onFilterWithPendingCardPayment() {
      this.filtersModel.filterOrderNoCardPayment.value = this.filtersModel.filterOrderNoCardPayment.value === 2 ? this.filtersModel.filterOrderNoCardPayment.value = 0 : this.filtersModel.filterOrderNoCardPayment.value = 2
    },

    orderIsWithUnpaidCardTransactions(o) {
      return o?.PaymentTransactions?.some(trx => (PaymentTypeEnum.isDeliveryCard(trx.PaymentType) || PaymentTypeEnum.isEftPosPaymentType(trx.PaymentType)) && trx?.Status !== 'APPROVED')
    },

    customSearch(value, search, item) {
      if (!search) return

      const searchString = greekUtils.toGreeklish(String(search).toLocaleLowerCase(this.$i18n.locale))

      const itemString = [
        item.Id && String(item.Id),
        item.OrderCode && String(item.OrderCode),
        item.OrderCode && String(item.OrderCode).replace('-', ''),
        item.Email && String(item.Email),
        item.Address && greekUtils.toGreeklish(JSON.stringify(item.Address).toLocaleLowerCase(this.$i18n.locale)),
        item.FirstName && greekUtils.toGreeklish(String(item.FirstName).toLocaleLowerCase(this.$i18n.locale)),
        item.LastName && greekUtils.toGreeklish(String(item.LastName).toLocaleLowerCase(this.$i18n.locale)),
        item.StaffFirstname && greekUtils.toGreeklish(String(item.StaffFirstname).toLocaleLowerCase(this.$i18n.locale)),
        item.StaffLastname && greekUtils.toGreeklish(String(item.StaffLastname).toLocaleLowerCase(this.$i18n.locale)),
        item.Table?.TableAreaName && greekUtils.toGreeklish(String(item.Table.TableAreaName).toLocaleLowerCase(this.$i18n.locale)),
        item.Table?.TableName && greekUtils.toGreeklish(String(item.Table.TableName).toLocaleLowerCase(this.$i18n.locale)),
        item.Telephone && String(item.Telephone),
        item.Telephone2 && String(item.Telephone2),
        item.Telephone3 && String(item.Telephone3)
      ].filter(Boolean).join(' ')

      return itemString.includes(searchString)
    },

    getPrintNotifyStatus(print) {
      const label = (this.getPrintNotifyType(print.Type)?.Title ?? print.Type) + (print.Group && ` [${print.Group}]`)
      const avatarIcon = print.ReceivedAt ? 'check' : 'close'
      const avatarColor = print.ReceivedAt ? 'success darken-1' : 'red darken-1'
      const color = this.getPrintNotifyType(print.Type).Color
      const tooltip = print.ReceivedAt ? '' : ''

      return {
        Label      : label,
        Tooltip    : tooltip,
        Color      : color,
        AvatarColor: avatarColor,
        AvatarIcon : avatarIcon
      }
    },

    getPrintNotifyType(type) {
      return this.$te(`Order.PrintNotifyType.${type}`) ? this.$t(`Order.PrintNotifyType.${type}`) : null
    },

    getDeliveryStaffById(deliveryStaffId) {
      return this.deliveryStaffList.find(staff => parseInt(staff.Id) === parseInt(deliveryStaffId))
    },
    deliveryStaffHasActiveShift(order) {
      return order && order.DeliveryStaffHasActiveShift
    },
    showRemoveOrderFromDeliveryStaffDialog(order) {
      if (order && this.deliveryStaffHasActiveShift(order)) {
        this.selectedOrder = order
        this.confirmDialogRemoveOrderFromDeliveryStaffVisible = true
      }
    },
    removeOrderFromDeliveryStaff() {
      const selectedDeliveryStaff = this.getDeliveryStaffById(this.selectedOrder.DeliveryStaffId)

      if (selectedDeliveryStaff && this.selectedOrder) {
        const selectedOrder = selectedDeliveryStaff.ShiftOrders.find(order => parseInt(order.Id) === parseInt(this.selectedOrder.Id))

        if (selectedOrder) {
          window.callAS(window.SocketCommand.Staff.Delivery.Shift.RemoveOrder, {
            DeliveryStaffShiftOrderId: selectedOrder.DeliveryStaffShiftOrderId,
            DeliveryStaffShiftId     : selectedDeliveryStaff.DeliveryStaffShiftId,
            OrderId                  : selectedOrder.Id
          }, window.SocketCommand.Staff.Delivery.All)
        }
      }
      this.selectedOrder = null
    },
    showSelectOrderTypeTimeDialog(order) {
      if (parseInt(order.StatusId) === OrderStatusEnum.Received) {
        this.executeWithPinPermissionManager(PinManagerEnum.PosChangeOrderTimeDuringAccept, (success) => {
          if (!success) return
          this.$bus.$emit('order-type-time-dialog', order)
        })
      } else {
        this.executeWithPinPermissionManager(PinManagerEnum.PosChangeOrderTimeAfterAccept, (success) => {
          if (!success) return
          this.$bus.$emit('order-type-time-dialog', order)
        })
      }
    },
    showAddOrderToDeliveryStaff(order) {
      this.selectedOrder = order
      this.selectedDeliveryStaffDialogVisible = true
    },
    showAcceptOrderDialog(order) {
      this.$bus.$emit('show-order-accept-dialog', order)
    },
    showOrderMapDialog() {
      this.ordersMapDialogVisible = true
    },

    orderPaymentDialogAvailable(order) {
      if (
        SourceTypeEnum.ONLINE_SOURCES.includes(parseInt(order.SourceTypeId)) ||
          SourceTypeEnum.TABLE === parseInt(order.SourceTypeId) ||
          order.StatusId === OrderStatusEnum.Canceled ||
          (order.DeletedAt && order.StatusId === OrderStatusEnum.Canceled) ||
          this.orderHasFiscalDocument(order)
      ) {
        return false
      }

      return true
    },

    showOrderPaymentDialog(order) {
      this.selectedOrder = order

      this.executeWithPinPermissionManager(PinManagerEnum.PosPayments, (success) => {
        if (!success) return
        this.orderPaymentDialogVisible = true
      })
    },

    onReceiptUpdate(data) {
      if (!data?.command) return
      const command = SocketCommand.commandToClientFormat(data?.command)
      let orderId = null

      if (command === SocketCommand.Receipt.Generate) orderId = data?.Id || null
      if (command === SocketCommand.Receipt.Cancel) orderId = data?.OrderId || null
      if (command === SocketCommand.Receipt.ConvertToInvoice) orderId = data?.OrderId || null

      if (!orderId) return

      this.getOrderUpdate(orderId)
    },

    onPaymentTransactionResult(data) {
      if (data?.order_id) {
        this.getOrderUpdate(data.order_id)
      } else {
        this.getData()
      }
    },

    getOrderUpdate(orderId) {
      if (!orderId) return

      SocketOrderHelper.getDailyOrder(orderId)
    },

    getData(payload = {}) {
      this.getDataInternal(payload)
      window.callAS(window.SocketCommand.Order.Counts, {})
      // console.log((new Error()).stack)
      // console.trace('>>>>> getData')
    },
    setData(data) {
      //console.log(data)
      this.setDataInternal(data)
    },

    onUpdateSelectedOrder(data, payload) {
      const orderId = payload?.orderId || payload?.order_id || data?.order?.Id || data?.order?.id || null
      const orders = Array.isArray(data) ? data : data?.order ? [data?.order] : []

      if (!orderId || !this.selectedOrder) return

      const oldOrder = clone(this.selectedOrder)
      const updatedOrder = orders?.find(order => parseInt(order.Id) === parseInt(orderId))
      if (this.selectedOrder?.Id === updatedOrder?.Id) {

        oldOrder.PaymentTransactions.forEach(oldTrx => {
          const updatedOrderTransaction = updatedOrder.PaymentTransactions.find(newTrx => newTrx.TransactionId === oldTrx.TransactionId)
          if (updatedOrderTransaction) {
            updatedOrderTransaction.WaitingPos = oldTrx.WaitingPos
            //updatedOrderTransaction.busy = oldTrx.busy
          }
        })

        this.selectedOrder = updatedOrder
      }
    },

    showSelectEmailDialog(extraData) {
      this.emailSelectDialogExtraData = extraData || null
      this.emailSelectDialogVisible = true
    },
    onEmailSelected(emails, extraData) {
      if (!Array.isArray(emails) || !emails?.length) return

      if (extraData?.type === 'receiptTotalsZ') {
        this.SendEmailReceiptsReport(emails, extraData?.datePeriod || null, extraData?.staffId || null, extraData?.guid || null, extraData?.withoutOpenHours ?? false)
      } else {
        this.SendEmailOrdersReport(emails, extraData?.datePeriod || null)
      }
    },

    showSelectDateRangeDialog(printer, type = 'receiptTotalsZ', action = 'print') {
      this.selectedPrinter = {
        printer: printer,
        action : action,
        type   : type
      }
      this.selectDateRangeDialogVisible = true
    },
    onPrintReceiptTotalsZDateRangeSelected(data) {
      const {datePeriod, withoutOpenHours, staffId, guid} = data

      if (this.selectedPrinter.type === 'receiptTotalsZ') {
        if (this.selectedPrinter.action === 'print') {
          this.printReceiptTotalsZ(datePeriod || null, staffId || null, guid || null, withoutOpenHours ?? false, this.selectedPrinter.printer)
        } else if (this.selectedPrinter.action === 'email') {
          this.showSelectEmailDialog({
            type            : this.selectedPrinter.type,
            datePeriod      : datePeriod || null,
            staffId         : staffId || null,
            guid            : guid || null,
            withoutOpenHours: withoutOpenHours ?? false
          })
        } else if (this.selectedPrinter.action === 'pdf') {
          this.downloadPdfReceiptsReport(datePeriod || null, staffId || null, guid || null, withoutOpenHours ?? false)
        }
      } else {
        if (this.selectedPrinter.action === 'print') {
          this.printOrderTotalsZ(datePeriod || null, this.selectedPrinter.printer)
        } else if (this.selectedPrinter.action === 'email') {
          this.showSelectEmailDialog({
            type      : this.selectedPrinter.type,
            datePeriod: datePeriod || null
          })
        } else if (this.selectedPrinter.action === 'pdf') {
          this.downloadPdfOrdersReport(datePeriod || null)
        }
      }
      this.selectedPrinter = null
    },

    getMachineGroupMachines(guid) {
      const printGuids = guid || []
      return this.appMachines.filter(m => printGuids.includes(m.guid))
    },

    getMachineGroupMachineNames(guid) {
      return this.getMachineGroupMachines(guid).map(m => m.alias || m.hostname) || []
    },

    getOrderMachineGroups(print) {
      const printGuids = print?.Guid || []
      return this.appMachineGroups.filter(m => printGuids.includes(m.Id))
    },

    onDataTableCurrentItemsChange(orders) {
      /*
      for (let i = 0; i < orders.length; i++) {
        orders[i].disabled = true
      }
      this.$forceUpdate()
      setTimeout(() => {
        for (let i = 0; i < orders.length; i++) {
          orders[i].disabled = false
        }
        this.$forceUpdate()
      }, 2000)
      */
    }
  }
}
</script>

<style scoped>
:deep(.receipts-badge > .v-badge__wrapper > span) {
  height: 16px;
  min-width: 16px;
  font-size: 11px;
  font-weight: bold;
  line-height: 8px;
  padding: 5px 4px;
}
</style>
